import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useContacts } from "./use-contacts";
import { useCompanies } from "./use-companies";
import { useDeals } from "./use-deals";

export const useRoleBasedAccess = () => {
  const { session } = useAuth();

  // Get user role and organization with better error handling
  const userRoleQuery = useQuery({
    queryKey: ["user-role", session?.user?.id, session?.access_token],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      console.log("🔍 [RoleBasedAccess] Fetching user role for:", session.user.email);
      
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role, organization_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (error) {
          console.error("❌ [RoleBasedAccess] Error fetching user role:", error);
          return { role: 'standard', organization_id: null };
        }
        
        const result = data || { role: 'standard', organization_id: null };
        console.log("✅ [RoleBasedAccess] User role data:", result);
        return result;
      } catch (err) {
        console.error("❌ [RoleBasedAccess] Unexpected error:", err);
        return { role: 'standard', organization_id: null };
      }
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60, // 1 minute cache
    refetchOnMount: true,
    placeholderData: (previousData) => previousData,
    retry: 1,
    retryDelay: 1000
  });

  const userRole = userRoleQuery.data?.role || 'standard';
  const userOrganizationId = userRoleQuery.data?.organization_id;

  // Partner access is an add-on capability determined by the partner record,
  // not by a separate role. This lets an organisation user also be a partner
  // without creating fragile multi-role rows.
  const partnerQuery = useQuery({
    queryKey: ["partner-id", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      console.log("🔍 [RoleBasedAccess] Checking partner record for user:", session.user.email);
      
      try {
        const { data, error } = await supabase
          .from('crm_partners')
          .select('id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (error) {
          console.error("❌ [RoleBasedAccess] Error fetching partner record:", error);
          return null;
        }
        
        console.log("✅ [RoleBasedAccess] Partner record:", data?.id || null);
        return data?.id || null;
      } catch (err) {
        console.error("❌ [RoleBasedAccess] Unexpected error fetching partner record:", err);
        return null;
      }
    },
    enabled: !!session?.user?.id && !userRoleQuery.isLoading,
    retry: 1,
    retryDelay: 1000
  });

  const partnerId = partnerQuery.data;
  const isLoading = userRoleQuery.isLoading || partnerQuery.isLoading;

  const isSystemAdmin = userRole === 'superuser';
  const isPartner = !!partnerQuery.data;
  const isOrgAdmin = userRole === 'admin';
  const isAdmin = userRole === 'admin' || userRole === 'superuser';
  const isStaff = userRole === 'admin' || userRole === 'superuser';
  const isInternalStaff = userRole === 'internal_staff';
  const isAdvisor = userRole === 'advisor';
  const isProduction = userRole === 'production';
  const isArchived = userRole === 'archived';

  console.log("🔍 [RoleBasedAccess] Final role calculations:", { 
    userRole, 
    isSystemAdmin, 
    isPartner, 
    isOrgAdmin, 
    isAdmin, 
    isStaff,
    isInternalStaff,
    isArchived,
    partnerId, 
    userOrganizationId 
  });

  return {
    userRole,
    partnerId,
    userOrganizationId,
    isPartner,
    isOrgAdmin,
    isSystemAdmin,
    isAdmin,
    isStaff,
    isInternalStaff,
    isArchived,
    isLoading
  };
};

export const usePartnerContacts = () => {
  const { partnerId, userRole } = useRoleBasedAccess();
  
  console.log("🔍 [PartnerContacts] Hook called with partnerId:", partnerId, "userRole:", userRole);
  
  return useQuery({
    queryKey: ["partner-contacts", partnerId],
    queryFn: async () => {
      console.log("🔍 [PartnerContacts] Fetching contacts for partner:", partnerId);
      if (!partnerId) {
        console.log("⚠️ [PartnerContacts] No partnerId, returning empty array");
        return [];
      }
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(`
            *,
            organizations!fk_profiles_organization(id, name, company_id, relationship_status),
            internal_owner:crm_staff!fk_profiles_internal_owner(id, first_name, last_name),
            external_owner:crm_partners!fk_profiles_external_owner(id, first_name, last_name)
          `)
          .eq('external_owner_id', partnerId)
          .not('first_name', 'is', null)
          .not('last_name', 'is', null);

        if (error) {
          console.error("❌ [PartnerContacts] Error fetching partner contacts:", error);
          // If it's a permission error, return empty array instead of throwing
          if (error.code === 'PGRST301' || error.message.includes('permission')) {
            console.log("🔒 [PartnerContacts] Permission denied, returning empty array");
            return [];
          }
          throw error;
        }

        console.log("✅ [PartnerContacts] Successfully fetched contacts:", data?.length || 0);
        return data || [];
      } catch (error) {
        console.error("❌ [PartnerContacts] Unexpected error:", error);
        return [];
      }
    },
    enabled: !!partnerId,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
};

export const usePartnerOrganizations = () => {
  const { partnerId } = useRoleBasedAccess();
  
  return useQuery({
    queryKey: ["partner-organizations", partnerId],
    queryFn: async () => {
      if (!partnerId) return [];
      
      // Get organizations from contacts owned by partner and deals assigned to partner
      const { data: contactOrgs, error: contactError } = await supabase
        .from('profiles')
        .select('organization_id, organizations!fk_profiles_organization(*)')
        .eq('external_owner_id', partnerId)
        .not('organization_id', 'is', null);

      if (contactError) {
        console.error("Error fetching contact organizations:", contactError);
      }

      const { data: dealOrgs, error: dealError } = await supabase
        .from('crm_deals')
        .select('organization_id, organizations!crm_deals_organization_id_fkey(*)')
        .eq('assigned_to_partner_id', partnerId)
        .not('organization_id', 'is', null);

      if (dealError) {
        console.error("Error fetching deal organizations:", dealError);
      }

      // Organizations the partner owns directly (e.g. created by them)
      const { data: ownedOrgs, error: ownedError } = await supabase
        .from('organizations')
        .select('*')
        .eq('external_owner_id', partnerId);

      if (ownedError) {
        console.error("Error fetching partner-owned organizations:", ownedError);
      }

      // Combine and deduplicate organizations
      const allOrgs = new Map();

      (ownedOrgs || []).forEach(org => {
        allOrgs.set(org.id, org);
      });
      
      
      (contactOrgs || []).forEach(item => {
        if (item.organizations) {
          allOrgs.set(item.organizations.id, item.organizations);
        }
      });
      
      (dealOrgs || []).forEach(item => {
        if (item.organizations) {
          allOrgs.set(item.organizations.id, item.organizations);
        }
      });

      return Array.from(allOrgs.values());
    },
    enabled: !!partnerId
  });
};

// Organization-scoped hooks for admin users
export const useOrgContacts = () => {
  const { userOrganizationId, userRole } = useRoleBasedAccess();
  
  return useQuery({
    queryKey: ["org-contacts", userOrganizationId],
    queryFn: async () => {
      if (!userOrganizationId) return [];
      
      console.log("🔍 [OrgContacts] Fetching contacts for organization:", userOrganizationId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          organizations!fk_profiles_organization(id, name, company_id, relationship_status),
          internal_owner:crm_staff!fk_profiles_internal_owner(id, first_name, last_name),
          external_owner:crm_partners!fk_profiles_external_owner(id, first_name, last_name)
        `)
        .eq('organization_id', userOrganizationId)
        .not('first_name', 'is', null)
        .not('last_name', 'is', null);

      if (error) {
        console.error("❌ [OrgContacts] Error fetching org contacts:", error);
        return [];
      }

      console.log("✅ [OrgContacts] Successfully fetched contacts:", data?.length || 0);
      return data || [];
    },
    enabled: !!userOrganizationId && userRole === 'admin'
  });
};

export const useOrgOrganizations = () => {
  const { userOrganizationId, userRole } = useRoleBasedAccess();
  
  return useQuery({
    queryKey: ["org-organizations", userOrganizationId],
    queryFn: async () => {
      if (!userOrganizationId) return [];
      
      console.log("🔍 [OrgOrganizations] Fetching organization:", userOrganizationId);
      
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', userOrganizationId);

      if (error) {
        console.error("❌ [OrgOrganizations] Error fetching organization:", error);
        return [];
      }

      console.log("✅ [OrgOrganizations] Successfully fetched organization");
      return data || [];
    },
    enabled: !!userOrganizationId && userRole === 'admin'
  });
};

export const useOrgDeals = () => {
  const { userOrganizationId, userRole } = useRoleBasedAccess();
  
  return useQuery({
    queryKey: ["org-deals", userOrganizationId],
    queryFn: async () => {
      if (!userOrganizationId) return [];
      
      console.log("🔍 [OrgDeals] Fetching deals for organization:", userOrganizationId);
      
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(*),
          organization:organizations!crm_deals_organization_id_fkey(*),
          stage:crm_deal_stages!crm_deals_stage_id_fkey(*)
        `)
        .eq('organization_id', userOrganizationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("❌ [OrgDeals] Error fetching org deals:", error);
        return [];
      }

      console.log("✅ [OrgDeals] Successfully fetched deals:", data?.length || 0);
      return data || [];
    },
    enabled: !!userOrganizationId && userRole === 'admin'
  });
};

export const useRoleBasedData = () => {
  const { isPartner, partnerId, userRole, isOrgAdmin, isSystemAdmin, isInternalStaff } = useRoleBasedAccess();
  
  console.log("🔍 [useRoleBasedData] Called with:", { isPartner, partnerId, userRole, isOrgAdmin, isSystemAdmin, isInternalStaff });
  
  // Use appropriate hooks based on role
  const allContacts = useContacts();
  const partnerContacts = usePartnerContacts();
  const orgContacts = useOrgContacts();
  
  const allOrganizations = useCompanies();
  const partnerOrganizations = usePartnerOrganizations();
  const orgOrganizations = useOrgOrganizations();
  
  const allDeals = useDeals();
  const partnerDeals = useDeals(partnerId || undefined);
  const orgDeals = useOrgDeals();
  
  console.log("🔍 [useRoleBasedData] Hook states:", {
    allContactsLoading: allContacts.isLoading,
    partnerContactsLoading: partnerContacts.isLoading,
    orgContactsLoading: orgContacts.isLoading,
    allContactsData: allContacts.data?.length,
    partnerContactsData: partnerContacts.data?.length,
    orgContactsData: orgContacts.data?.length
  });
  
  // Determine which data to return based on role hierarchy.
  // Superusers/internal staff always get the full view; partner scoping
  // only applies when the user is not in an elevated role.
  let contacts, organizations, deals, isLoading;
  
  if (isSystemAdmin || isInternalStaff) {
    // Superusers and internal staff see all data
    contacts = allContacts;
    organizations = allOrganizations;
    deals = allDeals;
    isLoading = allContacts.isLoading || allOrganizations.isLoading || allDeals.isLoading;
  } else if (isPartner) {
    // Partners see their managed data
    contacts = partnerContacts;
    organizations = partnerOrganizations;
    deals = partnerDeals;
    isLoading = partnerContacts.isLoading || partnerOrganizations.isLoading || partnerDeals.isLoading;
  } else if (isOrgAdmin) {
    // Organization admins see only their organization's data
    contacts = orgContacts;
    organizations = orgOrganizations;
    deals = orgDeals;
    isLoading = orgContacts.isLoading || orgOrganizations.isLoading || orgDeals.isLoading;
  } else {
    // Standard users see all data (for now - could be restricted further)
    contacts = allContacts;
    organizations = allOrganizations;
    deals = allDeals;
    isLoading = allContacts.isLoading || allOrganizations.isLoading || allDeals.isLoading;
  }
  
  return {
    contacts,
    organizations,
    deals,
    isLoading
  };
};